/**
 * Панель действий над выделенными сообщениями.
 *
 * Появляется только когда что-то выделено. Пересылка требует выбрать чат, и
 * список ограничен чатами ТОГО ЖЕ аккаунта: Telegram пересылает внутри сессии
 * и связи между диалогами разных номеров не знает. Показывать недоступные
 * варианты и отвергать их после нажатия — худший вариант, чем не показывать
 * вовсе.
 */

import { useMemo, useState } from 'react';
import type { TelegramChatDto } from '@tw/shared-types';
import { Button } from '@/components/ui/button';
import { SearchIcon } from './icons.tsx';
import { chatTitle } from './chatTitle.js';

interface ForwardBarProps {
  count: number;
  sourceChat: TelegramChatDto;
  chats: TelegramChatDto[];
  busy: boolean;
  onForward: (targetChatId: string) => void;
  onCancel: () => void;
}

export function ForwardBar({
  count,
  sourceChat,
  chats,
  busy,
  onForward,
  onCancel,
}: ForwardBarProps) {
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');

  const targets = useMemo(() => {
    const q = query.trim().toLowerCase();
    return chats
      .filter((c) => c.account.id === sourceChat.account.id && c.id !== sourceChat.id)
      .filter((c) => (q ? (c.title ?? '').toLowerCase().includes(q) : true))
      .slice(0, 40);
  }, [chats, sourceChat, query]);

  return (
    <div className="relative flex flex-none items-center gap-2 border-t border-border bg-card px-5 py-2.5 text-sm">
      <span className="font-medium">Выделено: {count}</span>

      <Button size="sm" onClick={() => setPicking((v) => !v)} disabled={busy}>
        Переслать
      </Button>

      <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
        Отмена
      </Button>

      {picking && (
        <div className="absolute bottom-[calc(100%+8px)] left-5 z-30 w-80 rounded-2xl border border-border bg-popover p-2 shadow-2xl">
          <div className="relative mb-1.5 flex items-center">
            <SearchIcon className="pointer-events-none absolute left-2.5 size-4 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Куда переслать"
              className="h-8 w-full rounded-lg border border-border bg-background pl-8 pr-2 text-sm outline-none focus:border-ring"
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {targets.length === 0 && (
              <p className="p-3 text-xs leading-relaxed text-muted-foreground">
                Нет других чатов этого аккаунта. Переслать в чат другого номера нельзя:
                Telegram не связывает диалоги разных аккаунтов.
              </p>
            )}

            {targets.map((chat) => (
              <button
                key={chat.id}
                type="button"
                disabled={busy}
                onClick={() => {
                  setPicking(false);
                  onForward(chat.id);
                }}
                className="w-full truncate rounded-lg px-2.5 py-1.5 text-left text-sm transition-colors hover:bg-accent disabled:opacity-50"
              >
                {chatTitle(chat.title)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
